/**
 * Fills snippets/open-now.liquid: whether the shop is open, in UK time.
 *
 * The hours come from the element's own data attributes, one per day (data-monday to
 * data-sunday), each "HH:MM-HH:MM" or empty for closed: the same `store_location` fields the
 * printed hours are made from. UK time, because the shop is in Frome and a visitor elsewhere must
 * still get Frome's answer.
 */
const ZONE = 'Europe/London';
const KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday', 'sunday'];
const NAMES = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];
const WEEKDAY = { Mon: 0, Tue: 1, Wed: 2, Thu: 3, Fri: 4, Sat: 5, Sun: 6 };

// An empty field means closed. Number('') is 0, so without the pattern check an empty Sunday
// parsed as midnight and Saturday evening said "Opens tomorrow at 12am" (caught 2026-09-29).
const minutes = (hhmm) => {
  const t = String(hhmm || '').trim();
  if (!/^\d{1,2}:\d{2}$/.test(t)) return NaN;
  const [h, m] = t.split(':').map(Number);
  return h * 60 + m;
};

const spoken = (mins) => {
  const h = Math.floor(mins / 60);
  const m = mins % 60;
  const h12 = ((h + 11) % 12) + 1;
  return `${h12}${m ? '.' + String(m).padStart(2, '0') : ''}${h < 12 ? 'am' : 'pm'}`;
};

function londonNow() {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: ZONE,
    weekday: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hourCycle: 'h23',
  }).formatToParts(new Date());
  const get = (t) => parts.find((p) => p.type === t)?.value;
  return { day: WEEKDAY[get('weekday')], mins: Number(get('hour')) * 60 + Number(get('minute')) };
}

class OpenNow extends HTMLElement {
  connectedCallback() {
    const week = KEYS.map((key) => {
      const [opens, closes] = String(this.dataset[key] || '').split('-');
      const o = minutes(opens);
      const c = minutes(closes);
      return Number.isFinite(o) && Number.isFinite(c) ? { opens: o, closes: c } : null;
    });
    if (!week.some(Boolean)) return;

    const now = londonNow();
    const today = week[now.day];
    const text = this.querySelector('.open-now__text');

    if (today && now.mins >= today.opens && now.mins < today.closes) {
      this.dataset.state = 'open';
      text.textContent = this.dataset.openText.replace('[time]', spoken(today.closes));
    } else {
      let found = null;
      for (let i = 0; i < 8 && !found; i++) {
        const d = (now.day + i) % 7;
        const slot = week[d];
        if (slot && (i > 0 || slot.opens > now.mins)) {
          const when = i === 0 ? this.dataset.today : i === 1 ? this.dataset.tomorrow : NAMES[d];
          found = { when, opens: slot.opens };
        }
      }
      if (!found) return;
      this.dataset.state = 'closed';
      text.textContent = this.dataset.closedText.replace('[day]', found.when).replace('[time]', spoken(found.opens));
    }
    this.hidden = false;
  }
}

if (!customElements.get('open-now')) customElements.define('open-now', OpenNow);
